import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuthService } from '@/hooks/use-auth-service';
import { queryClient } from '@/lib/query-client';

const loginSchema = z.object({
  loginname: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export interface LoginFormProps {
  /** Whether the form is embedded in a modal context */
  isModal?: boolean;
  /** Called when login is successful */
  onSuccess?: () => void;
  /** Called when "Forgot Password" link is clicked */
  onForgotPassword?: () => void;
  /** Additional CSS classes */
  className?: string;
}

export function LoginForm({
  isModal = false,
  onSuccess,
  onForgotPassword,
  className,
}: LoginFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const authService = useAuthService();

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      loginname: '',
      password: '',
    },
  });

  // Dev shortcut: Ctrl+L (desktop) or tap label (mobile) fills test credentials.
  const fillDevCredentials = () => {
    form.setValue('loginname', 'midge');
    form.setValue('password', '1234pass');
    submitButtonRef.current?.focus();
  };

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'l') {
        e.preventDefault();
        fillDevCredentials();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [form]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Convert form data to AuthCredentials format
      const credentials = {
        type: 'password' as const,
        loginname: data.loginname,
        password: data.password,
      };
      await authService.signIn(credentials);
      await queryClient.resetQueries();
      onSuccess?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={className}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="loginname"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="johndoe"
                    autoComplete="username"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <div className="relative w-full">
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      disabled={isLoading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3
                        -translate-y-1/2 transition-colors"
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                      <span className="sr-only">
                        {showPassword ? 'Hide password' : 'Show password'}
                      </span>
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col space-y-3">
            <Button
              ref={submitButtonRef}
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isModal ? 'Authenticating...' : 'Signing in...'}
                </>
              ) : (
                <>{isModal ? 'Re-authenticate' : 'Sign in'}</>
              )}
            </Button>

            {onForgotPassword && (
              <Button
                type="button"
                variant="link"
                onClick={onForgotPassword}
                className="text-muted-foreground hover:text-foreground text-sm"
                disabled={isLoading}
              >
                Forgot your password?
              </Button>
            )}
          </div>

          {import.meta.env.DEV && (
            <button
              type="button"
              onClick={fillDevCredentials}
              className="text-muted-foreground hover:text-foreground mt-2 w-full cursor-pointer
                text-center text-xs"
            >
              Press{' '}
              <kbd className="bg-muted rounded px-1 py-0.5 font-mono text-[10px]">
                Ctrl+L
              </kbd>{' '}
              or tap here to fill dev credentials
            </button>
          )}
        </form>
      </Form>
    </div>
  );
}

export default LoginForm;
